import {
  PointerEvent as ReactPointerEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Testimonial } from '../testimonials.data';
import {
  REGISTRATION_TESTIMONIAL_CARD_GAP_PX,
  REGISTRATION_TESTIMONIAL_CARD_WIDTH_PX,
} from '../layout.constants';
import * as styles from './Testimonials.css';
import { TestimonialCard } from './TestimonialCard';

const AUTOPLAY_INTERVAL_MS = 7000;
const CARD_WIDTH_PX = REGISTRATION_TESTIMONIAL_CARD_WIDTH_PX;
const CARD_GAP_PX = REGISTRATION_TESTIMONIAL_CARD_GAP_PX;
const SWIPE_THRESHOLD_RATIO = 0.3;
const CLICK_SUPPRESS_THRESHOLD_PX = 8;

type TestimonialsCarouselProps = {
  testimonials: Array<Testimonial>;
};

const normalizeCarouselIndex = (
  index: number,
  testimonialsCount: number,
): number => {
  if (testimonialsCount <= 1) {
    return 0;
  }

  const lowerBound = testimonialsCount;
  const upperBound = testimonialsCount * 2;
  let nextIndex = index;

  while (nextIndex < lowerBound) {
    nextIndex += testimonialsCount;
  }

  while (nextIndex >= upperBound) {
    nextIndex -= testimonialsCount;
  }

  return nextIndex;
};

const shiftCarouselIndex = (
  index: number,
  testimonialsCount: number,
  delta: number,
): number => {
  return normalizeCarouselIndex(index, testimonialsCount) + delta;
};

const toBufferedTestimonials = (
  testimonials: Array<Testimonial>,
): Array<Testimonial> => {
  if (testimonials.length <= 1) {
    return testimonials;
  }

  return [...testimonials, ...testimonials, ...testimonials];
};

export const TestimonialsCarousel = ({
  testimonials,
}: TestimonialsCarouselProps) => {
  const [carouselIndex, setCarouselIndex] = useState(testimonials.length);
  const [isTransitionEnabled, setIsTransitionEnabled] = useState(true);
  const [isPointerDown, setIsPointerDown] = useState(false);
  const [dragOffsetPx, setDragOffsetPx] = useState(0);
  const autoplayTimeoutRef = useRef<number | null>(null);
  const clickSuppressTimeoutRef = useRef<number | null>(null);
  const activePointerIdRef = useRef<number | null>(null);
  const dragStartClientXRef = useRef<number | null>(null);
  const pointerDownSlideIndexRef = useRef<number | null>(null);
  const interactionStartCarouselIndexRef = useRef<number | null>(null);
  const suppressClickRef = useRef(false);
  const testimonialsCount = testimonials.length;

  const bufferedTestimonials = useMemo(() => {
    return toBufferedTestimonials(testimonials);
  }, [testimonials]);

  useEffect(() => {
    if (testimonialsCount <= 1) {
      setCarouselIndex(0);
      setIsTransitionEnabled(false);
      return;
    }

    setCarouselIndex(testimonialsCount);
    setIsTransitionEnabled(false);
  }, [testimonialsCount]);

  useEffect(() => {
    if (testimonialsCount <= 1 || isPointerDown) {
      return;
    }

    autoplayTimeoutRef.current = window.setTimeout(() => {
      setIsTransitionEnabled(true);
      setCarouselIndex((index) =>
        shiftCarouselIndex(index, testimonialsCount, 1),
      );
    }, AUTOPLAY_INTERVAL_MS);

    return () => {
      if (autoplayTimeoutRef.current !== null) {
        window.clearTimeout(autoplayTimeoutRef.current);
        autoplayTimeoutRef.current = null;
      }
    };
  }, [carouselIndex, isPointerDown, testimonialsCount]);

  useEffect(() => {
    return () => {
      if (clickSuppressTimeoutRef.current !== null) {
        window.clearTimeout(clickSuppressTimeoutRef.current);
        clickSuppressTimeoutRef.current = null;
      }
    };
  }, []);

  if (bufferedTestimonials.length === 0) {
    return null;
  }

  const activeOffset = carouselIndex * (CARD_WIDTH_PX + CARD_GAP_PX);
  const trackTransform = `translate3d(calc(50% - ${
    CARD_WIDTH_PX / 2
  }px - ${activeOffset}px + ${dragOffsetPx}px), 0, 0)`;

  const handleTrackTransitionEnd = (): void => {
    if (testimonialsCount <= 1) {
      return;
    }

    const normalizedIndex = normalizeCarouselIndex(
      carouselIndex,
      testimonialsCount,
    );
    if (normalizedIndex !== carouselIndex) {
      setIsTransitionEnabled(false);
      setCarouselIndex(normalizedIndex);
    }
  };

  const handleSlideClick = (index: number): void => {
    if (suppressClickRef.current) {
      return;
    }

    if (testimonialsCount <= 1) {
      return;
    }

    if (index === carouselIndex - 1) {
      setIsTransitionEnabled(true);
      setCarouselIndex((currentIndex) =>
        shiftCarouselIndex(currentIndex, testimonialsCount, -1),
      );
      return;
    }

    if (index === carouselIndex + 1) {
      setIsTransitionEnabled(true);
      setCarouselIndex((currentIndex) =>
        shiftCarouselIndex(currentIndex, testimonialsCount, 1),
      );
    }
  };

  const finalizePointerInteraction = (
    finalClientX: number | null,
    pointerId: number,
  ): void => {
    if (activePointerIdRef.current !== pointerId) {
      return;
    }

    const startClientX = dragStartClientXRef.current;
    const finalOffset =
      startClientX === null || finalClientX === null
        ? dragOffsetPx
        : finalClientX - startClientX;
    const pointerDownSlideIndex = pointerDownSlideIndexRef.current;
    const interactionStartCarouselIndex =
      interactionStartCarouselIndexRef.current ?? carouselIndex;

    setIsPointerDown(false);
    activePointerIdRef.current = null;
    dragStartClientXRef.current = null;
    pointerDownSlideIndexRef.current = null;
    interactionStartCarouselIndexRef.current = null;

    const swipeThresholdPx = CARD_WIDTH_PX * SWIPE_THRESHOLD_RATIO;
    const shouldMoveToNext = finalOffset <= -swipeThresholdPx;
    const shouldMoveToPrevious = finalOffset >= swipeThresholdPx;

    setIsTransitionEnabled(true);
    setDragOffsetPx(0);

    if (testimonialsCount > 1) {
      if (shouldMoveToNext) {
        setCarouselIndex((currentIndex) =>
          shiftCarouselIndex(currentIndex, testimonialsCount, 1),
        );
      } else if (shouldMoveToPrevious) {
        setCarouselIndex((currentIndex) =>
          shiftCarouselIndex(currentIndex, testimonialsCount, -1),
        );
      } else if (
        !suppressClickRef.current &&
        pointerDownSlideIndex === interactionStartCarouselIndex - 1
      ) {
        setCarouselIndex((currentIndex) =>
          shiftCarouselIndex(currentIndex, testimonialsCount, -1),
        );
        suppressClickRef.current = true;
      } else if (
        !suppressClickRef.current &&
        pointerDownSlideIndex === interactionStartCarouselIndex + 1
      ) {
        setCarouselIndex((currentIndex) =>
          shiftCarouselIndex(currentIndex, testimonialsCount, 1),
        );
        suppressClickRef.current = true;
      }
    }

    if (suppressClickRef.current) {
      if (clickSuppressTimeoutRef.current !== null) {
        window.clearTimeout(clickSuppressTimeoutRef.current);
      }
      clickSuppressTimeoutRef.current = window.setTimeout(() => {
        suppressClickRef.current = false;
        clickSuppressTimeoutRef.current = null;
      }, 0);
    }
  };

  const handlePointerDown = (event: ReactPointerEvent<HTMLElement>): void => {
    if (testimonialsCount <= 1) {
      return;
    }

    if (autoplayTimeoutRef.current !== null) {
      window.clearTimeout(autoplayTimeoutRef.current);
      autoplayTimeoutRef.current = null;
    }

    activePointerIdRef.current = event.pointerId;
    dragStartClientXRef.current = event.clientX;
    interactionStartCarouselIndexRef.current = normalizeCarouselIndex(
      carouselIndex,
      testimonialsCount,
    );
    const pointerDownSlideElement = (event.target as HTMLElement).closest(
      '[data-slide-index]',
    );
    if (pointerDownSlideElement instanceof HTMLElement) {
      const parsedIndex = Number(pointerDownSlideElement.dataset.slideIndex);
      pointerDownSlideIndexRef.current = Number.isFinite(parsedIndex)
        ? parsedIndex
        : null;
    } else {
      pointerDownSlideIndexRef.current = null;
    }
    suppressClickRef.current = false;
    setIsTransitionEnabled(false);
    setCarouselIndex((currentIndex) =>
      normalizeCarouselIndex(currentIndex, testimonialsCount),
    );
    setIsPointerDown(true);
    setDragOffsetPx(0);
    if (typeof event.currentTarget.setPointerCapture === 'function') {
      event.currentTarget.setPointerCapture(event.pointerId);
    }
  };

  const handlePointerMove = (event: ReactPointerEvent<HTMLElement>): void => {
    if (!isPointerDown || activePointerIdRef.current !== event.pointerId) {
      return;
    }

    const startClientX = dragStartClientXRef.current;
    if (startClientX === null) {
      return;
    }

    const nextOffsetPx = event.clientX - startClientX;
    if (Math.abs(nextOffsetPx) > CLICK_SUPPRESS_THRESHOLD_PX) {
      suppressClickRef.current = true;
    }
    setDragOffsetPx(nextOffsetPx);
  };

  const handlePointerUp = (event: ReactPointerEvent<HTMLElement>): void => {
    finalizePointerInteraction(event.clientX, event.pointerId);
  };

  const handlePointerCancel = (event: ReactPointerEvent<HTMLElement>): void => {
    finalizePointerInteraction(null, event.pointerId);
  };

  const handleLostPointerCapture = (
    event: ReactPointerEvent<HTMLElement>,
  ): void => {
    finalizePointerInteraction(null, event.pointerId);
  };

  return (
    <section
      className={styles.testimonialCarousel}
      aria-label="Testimonials"
      data-dragging={isPointerDown ? 'true' : 'false'}
      onPointerDownCapture={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerCancel}
      onLostPointerCapture={handleLostPointerCapture}
    >
      <div className={styles.testimonialViewport}>
        <div
          className={styles.testimonialTrack}
          onTransitionEnd={handleTrackTransitionEnd}
          style={{ transform: trackTransform }}
          data-transition={
            isTransitionEnabled && !isPointerDown ? 'enabled' : 'disabled'
          }
        >
          {bufferedTestimonials.map((testimonial, index) => (
            <div
              key={`${testimonial.id}-${index}`}
              className={styles.testimonialSlide}
              data-slide-index={index}
              data-active={index === carouselIndex ? 'true' : 'false'}
              data-neighbor={
                Math.abs(index - carouselIndex) === 1 ? 'true' : 'false'
              }
              data-clickable={
                Math.abs(index - carouselIndex) === 1 ? 'true' : 'false'
              }
              onClick={() => handleSlideClick(index)}
            >
              <TestimonialCard testimonial={testimonial} />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
